import { type ClipboardEvent, type DragEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Attachment,
  attachmentDownloadUrl,
  attachmentPreviewUrl,
  fileCategory,
  formatBytes,
  type UploadItem,
  useAttachments,
  useAttachmentUploader,
  useDeleteAttachment,
} from '@/entities/attachment';
import { useClearCardCover, useSetCardCover } from '@/entities/board';
import { type CardDetail } from '@/entities/card';
import { FormError } from '@/shared/forms';
import { useErrorMessage } from '@/shared/i18n';
import { useTouchPrimary } from '@/shared/lib/media';
import { Button, ConfirmDialog, ErrorState, HelpRow, LoadingState } from '@/shared/ui';

import { useCardAbilities } from './abilities';
import styles from './Attachments.module.css';
import { AttachmentLightbox } from './AttachmentLightbox';

const ICONS = {
  image: '🖼',
  pdf: '📕',
  archive: '🗜',
  text: '📝',
  audio: '🎵',
  video: '🎬',
  document: '📄',
  other: '📎',
} as const;

interface Props {
  card: CardDetail;
  boardId: string;
}

/**
 * Files of a card: choose, drag & drop or paste (images from the clipboard), with progress;
 * download, preview, "use as cover" and delete. Files are always fetched through the API.
 */
export function AttachmentsSection({ card, boardId }: Props) {
  const { t, i18n } = useTranslation();
  const { editAttachments, viewAttachments } = useCardAbilities();
  const touch = useTouchPrimary();
  const attachments = useAttachments(card.id);
  const uploader = useAttachmentUploader(card.id, boardId);
  const remove = useDeleteAttachment(card.id, boardId);
  const setCover = useSetCardCover();
  const clearCover = useClearCardCover();
  const getErrorMessage = useErrorMessage();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [confirming, setConfirming] = useState<Attachment | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const coverId = card.cover?.kind === 'image' ? card.cover.attachment_id : null;

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setOver(false);
    uploader.add([...event.dataTransfer.files]);
  };
  const onPaste = (event: ClipboardEvent) => {
    const files = [...event.clipboardData.files];
    if (files.length === 0) return;
    event.preventDefault();
    uploader.add(files);
  };

  const list = attachments.data ?? [];
  const images = list.filter((a) => a.previewable);
  if (!viewAttachments) return null;

  return (
    <section
      className={styles.section}
      aria-labelledby={`attachments-${card.id}`}
      data-testid="attachments"
      onPaste={editAttachments ? onPaste : undefined}
    >
      <div className={styles.head}>
        <HelpRow topic={t('attachments.title')} topicId="content" text={t('help.attachments')}>
          <h4 id={`attachments-${card.id}`} className={styles.title}>
            {t('attachments.title')}
            {list.length > 0 && <span className={styles.count}> {list.length}</span>}
          </h4>
        </HelpRow>
        {editAttachments && (
          <Button
            size="sm"
            onClick={() => {
              input.current?.click();
            }}
          >
            {t('attachments.add')}
          </Button>
        )}
        <input
          ref={input}
          type="file"
          multiple
          className="visually-hidden"
          aria-label={t('attachments.add')}
          data-testid="attachment-input"
          onChange={(event) => {
            uploader.add([...(event.target.files ?? [])]);
            event.target.value = '';
          }}
        />
      </div>

      {editAttachments && (
        <div
          className={`${styles.drop} ${over ? styles.dropOver : ''}`}
          data-testid="attachment-drop"
          onDragOver={(event) => {
            event.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => {
            setOver(false);
          }}
          onDrop={onDrop}
        >
          {t(touch ? 'attachments.dropHintTouch' : 'attachments.dropHint')}
        </div>
      )}

      {uploader.items.length > 0 && (
        <ul className={styles.uploads} aria-label={t('attachments.uploads')}>
          {uploader.items.map((item) => (
            <UploadRow key={item.key} item={item} uploader={uploader} />
          ))}
        </ul>
      )}

      {attachments.isPending && <LoadingState />}
      {attachments.isError && (
        <ErrorState error={attachments.error} onRetry={() => void attachments.refetch()} />
      )}
      {attachments.isSuccess && list.length === 0 && uploader.items.length === 0 && (
        <p className={styles.empty}>{t('attachments.empty')}</p>
      )}
      {list.length > 0 && (
        <ul className={styles.list}>
          {list.map((attachment) => {
            const isCover = coverId === attachment.id;
            const category = fileCategory(attachment.mime_type, attachment.filename);
            return (
              <li key={attachment.id} className={styles.item} data-testid="attachment">
                {attachment.previewable ? (
                  <button
                    type="button"
                    className={styles.thumbButton}
                    aria-label={t('attachments.view', { name: attachment.filename })}
                    onClick={() => {
                      setViewing(attachment.id);
                    }}
                  >
                    <img
                      className={styles.thumb}
                      src={attachmentPreviewUrl(attachment.id, 'thumb')}
                      alt=""
                      loading="lazy"
                    />
                  </button>
                ) : (
                  <span className={styles.icon} aria-hidden>
                    {ICONS[category]}
                  </span>
                )}
                <div className={styles.meta}>
                  <span className={styles.name} title={attachment.filename}>
                    {attachment.filename}
                  </span>
                  <span className={styles.sub}>
                    {formatBytes(attachment.size_bytes, i18n.language)}
                    {attachment.width && attachment.height
                      ? ` · ${attachment.width}×${attachment.height}`
                      : ''}
                    {isCover && ` · ${t('attachments.isCover')}`}
                  </span>
                </div>
                <div className={styles.actions}>
                  <a
                    className={styles.link}
                    href={attachmentDownloadUrl(attachment.id)}
                    download={attachment.filename}
                  >
                    {t('attachments.download')}
                  </a>
                  {editAttachments &&
                    attachment.previewable &&
                    (isCover ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={clearCover.isPending}
                        onClick={() => {
                          clearCover.mutate(card.id);
                        }}
                      >
                        {t('attachments.removeCover')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={setCover.isPending}
                        onClick={() => {
                          setCover.mutate({
                            cardId: card.id,
                            cover: {
                              kind: 'image',
                              attachment_id: attachment.id,
                              position_x: 0.5,
                              position_y: 0.5,
                            },
                          });
                        }}
                      >
                        {t('attachments.useAsCover')}
                      </Button>
                    ))}
                  {editAttachments && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className={styles.danger}
                      onClick={() => {
                        setConfirming(attachment);
                      }}
                    >
                      {t('attachments.delete')}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <FormError error={remove.error ?? setCover.error ?? clearCover.error} />
      {remove.isError && <span className="visually-hidden">{getErrorMessage(remove.error)}</span>}

      {confirming && (
        <ConfirmDialog
          title={t('attachments.deleteTitle')}
          message={
            coverId === confirming.id
              ? t('attachments.deleteCoverMessage', { name: confirming.filename })
              : t('attachments.deleteMessage', { name: confirming.filename })
          }
          confirmLabel={t('common.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setConfirming(null);
          }}
          onConfirm={() => {
            remove.mutate(confirming.id, {
              onSettled: () => {
                setConfirming(null);
              },
            });
          }}
        />
      )}
      {viewing && (
        <AttachmentLightbox
          images={images}
          startId={viewing}
          onClose={() => {
            setViewing(null);
          }}
        />
      )}
    </section>
  );
}

function UploadRow({
  item,
  uploader,
}: {
  item: UploadItem;
  uploader: ReturnType<typeof useAttachmentUploader>;
}) {
  const { t, i18n } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const percent = Math.round(item.progress * 100);
  return (
    <li className={styles.upload} data-testid="upload-row" data-status={item.status}>
      <div className={styles.uploadTop}>
        <span className={styles.name} title={item.name}>
          {item.name}
        </span>
        <span className={styles.sub}>{formatBytes(item.size, i18n.language)}</span>
        <button
          type="button"
          className={styles.dismiss}
          aria-label={t(
            item.status === 'uploading' ? 'attachments.cancelUpload' : 'attachments.dismiss',
            { name: item.name },
          )}
          onClick={() => {
            uploader.remove(item.key);
          }}
        >
          ×
        </button>
      </div>
      {item.status === 'uploading' && (
        <progress
          className={styles.progress}
          value={percent}
          max={100}
          aria-label={t('attachments.progress', { name: item.name, percent })}
        />
      )}
      {item.status === 'done' && <span className={styles.ok}>✓ {t('attachments.uploaded')}</span>}
      {item.status === 'error' && (
        <div className={styles.error} role="alert">
          <span>{getErrorMessage(item.error)}</span>
          {!(
            typeof item.error === 'object' &&
            item.error !== null &&
            'code' in item.error &&
            item.error.code === 'MEDIA_TOO_LARGE'
          ) && (
            <Button
              size="sm"
              onClick={() => {
                uploader.retry(item.key);
              }}
            >
              {t('common.retry')}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
